import React from 'react';
import { useTelegramHandoff } from '../../hooks/useTelegramHandoff';
import { useTheme, useAssetUrl, usePreset } from '../../lib/ThemeContext';
import { TelegramIcon } from '../TelegramIcon';

interface AssistantCardProps {
  onBack: () => void;
  onClose: () => void;
  onNewChat?: () => void;
}

export const AssistantCard: React.FC<AssistantCardProps> = ({ onBack, onClose, onNewChat }) => {
  const { headerText, headerSubtitle, avatarUrl, telegramBotUrl, t } = useTheme();
  const assetUrl = useAssetUrl();
  const openTelegram = useTelegramHandoff(telegramBotUrl);
  const preset = usePreset();
  const isNevi = preset === 'nevi';

  const assistantName = headerText || t.chatHeader.defaultName;
  const subtitle = headerSubtitle || 'ИИ-ассистент';
  const neviDescription =
    'Our little three-eyed data guardian is ready to help you with your tasks and explain in detail the platform\u2019s capabilities and modern KYC procedures';
  const avatarSrc = avatarUrl || assetUrl(isNevi ? '/widget/nevi-avatar.webp' : '/widget/nv-avatar.webp');
  const font = isNevi ? "'WC-Satoshi', system-ui, sans-serif" : "'WC-Involve', sans-serif";
  const textColor = isNevi ? '#FBFBFB' : '#2E2E2E';
  const accentColor = isNevi ? '#F9AA66' : '#FE7338';
  const surfaceBg = isNevi ? 'rgba(251, 251, 251, 0.05)' : '#FFFFFF';
  const surfaceBorder = isNevi ? '1px solid rgba(251, 251, 251, 0.15)' : '1px solid rgba(46, 46, 46, 0.15)';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '36px',
        /* nevi: контент по центру упирался аватаром в верх карточки — толстый
           верхний padding при justify-content: center сдвигает блок вниз */
        padding: isNevi ? '56px 16px 16px' : '16px',
        width: '100%',
        height: '100%',
        background: isNevi ? '#212121' : '#FFFFFF',
        borderRadius: isNevi ? '24px' : '32px',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          right: '16px',
          top: '16px',
          width: '32px',
          height: '32px',
          background: surfaceBg,
          border: surfaceBorder,
          borderRadius: isNevi ? '6px' : '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 3,
          transition: 'opacity 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.8';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          style={{ width: '16px', height: '16px', color: textColor }}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Avatar + Name section */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          width: '100%',
          zIndex: 2,
        }}
      >
        {/* Large avatar */}
        <div
          style={{
            width: isNevi ? '164px' : '204px',
            height: isNevi ? '164px' : '204px',
            borderRadius: isNevi ? '32px' : '96px',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <img
            src={avatarSrc}
            alt={assistantName}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: isNevi ? '32px' : '96px' }}
          />
        </div>

        {/* Name + subtitle */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <span
            style={{
              fontFamily: font,
              fontWeight: isNevi ? 700 : 600,
              fontSize: isNevi ? '40px' : '24px',
              lineHeight: isNevi ? '52px' : '32px',
              color: textColor,
            }}
          >
            {assistantName}
          </span>
          <span
            style={{
              fontFamily: font,
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              color: accentColor,
            }}
          >
            {subtitle}
          </span>
        </div>

        {/* Description */}
        <p
          style={{
            fontFamily: font,
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            color: isNevi ? 'rgba(251, 251, 251, 0.7)' : '#2E2E2E',
            textAlign: 'center',
            margin: 0,
            width: '100%',
          }}
        >
          {isNevi
            ? neviDescription
            : t.assistantCard?.description ||
              `ИИ-ассистент готов помочь Вам в ваших задачах и подробно рассказать о возможностях платформы`}
        </p>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', zIndex: 1 }}>
        {telegramBotUrl && (
          <a
            href={telegramBotUrl}
            onClick={openTelegram}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: isNevi ? '4px' : '8px',
              padding: isNevi ? '6px 6px 6px 12px' : '8px 12px 8px 20px',
              background: isNevi ? '#F9AA66' : 'linear-gradient(150.69deg, #FE7338 0%, #EA3700 100%)',
              border: isNevi ? '1px solid rgba(251, 251, 251, 0.15)' : 'none',
              borderRadius: isNevi ? '8px' : '100px',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'opacity 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
            }}
          >
            <span
              style={{
                fontFamily: font,
                fontWeight: isNevi ? 700 : 500,
                fontSize: isNevi ? '14px' : '16px',
                lineHeight: isNevi ? '20px' : '24px',
                color: isNevi ? '#2B323B' : '#FFFFFF',
                whiteSpace: 'nowrap',
              }}
            >
              {t.assistantCard?.telegramButton || 'Перейти в чат-бот Telegram'}
            </span>
            {isNevi ? (
              <TelegramIcon size={18} fill="#2B323B" />
            ) : (
              <TelegramIcon size={24} fill="#FFFFFF" />
            )}
          </a>
        )}

        <button
          onClick={onBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isNevi ? '4px' : '8px',
            padding: isNevi ? '6px 12px 6px 6px' : '8px 20px 8px 12px',
            background: surfaceBg,
            border: surfaceBorder,
            borderRadius: isNevi ? '6px' : '100px',
            cursor: 'pointer',
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.8';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          {isNevi ? (
            /* Arrow - Left 2 - Iconly Pro */
            <svg viewBox="0 0 7.08339 12.9167" fill="none" style={{ width: '8px', height: '14px' }}>
              <path
                d="M0 6.45836C0 5.63338 0.489437 4.82543 1.0555 4.1439C1.6414 3.43853 2.40819 2.75332 3.15267 2.16067C3.90078 1.56512 4.64599 1.04781 5.20264 0.680363C5.48148 0.496295 5.71441 0.349114 5.87809 0.24742C5.95993 0.196578 6.02474 0.156432 6.06934 0.129419C6.09144 0.11603 6.10875 0.105593 6.12061 0.0984946C6.12649 0.0949715 6.13123 0.0922666 6.13444 0.0903566C6.136 0.0894262 6.13761 0.0884484 6.13851 0.0879151H6.13932V0.0871013H6.14014C6.43711 -0.0885734 6.82043 0.0098973 6.99626 0.306828C7.1609 0.585153 7.08485 0.939032 6.83024 1.12714L6.77409 1.16458C6.77173 1.16598 6.76772 1.16808 6.7627 1.17109C6.75255 1.17716 6.73673 1.18639 6.71631 1.19875C6.67547 1.22349 6.61481 1.26044 6.53727 1.30862C6.38195 1.40511 6.15913 1.54674 5.89111 1.72366C5.35404 2.07819 4.6408 2.57354 3.93066 3.13886C3.21703 3.70699 2.52517 4.33101 2.01742 4.94224C1.48977 5.57749 1.25 6.0938 1.25 6.45836C1.25 6.82316 1.48981 7.33923 2.01742 7.97447C2.52524 8.58588 3.21686 9.21047 3.93066 9.77867C4.64079 10.3439 5.35405 10.8386 5.89111 11.1931C6.15913 11.3699 6.38196 11.5116 6.53727 11.6081C6.61483 11.6563 6.67548 11.6932 6.71631 11.718C6.73673 11.7303 6.75255 11.7396 6.7627 11.7456C6.76769 11.7486 6.77174 11.7507 6.77409 11.7521L6.83024 11.7896C7.08485 11.9777 7.1609 12.3316 6.99626 12.6099C6.82045 12.9069 6.43714 13.0053 6.14014 12.8296H6.13932V12.8288H6.13851C6.1376 12.8283 6.13603 12.8273 6.13444 12.8264C6.13122 12.8244 6.12652 12.8218 6.12061 12.8182C6.10874 12.8111 6.09147 12.8007 6.06934 12.7873C6.02473 12.7603 5.95996 12.7201 5.87809 12.6693C5.7144 12.5676 5.4815 12.4204 5.20264 12.2364C4.64605 11.869 3.90069 11.3523 3.15267 10.7569C2.40819 10.1643 1.64141 9.47824 1.0555 8.77281C0.489469 8.09124 0 7.28343 0 6.45836Z"
                fill="#FBFBFB"
              />
            </svg>
          ) : (
            <svg
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              style={{ width: '24px', height: '24px', color: textColor }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          )}
          <span
            style={{
              fontFamily: font,
              fontWeight: isNevi ? 700 : 500,
              fontSize: isNevi ? '14px' : '16px',
              lineHeight: isNevi ? '20px' : '24px',
              color: textColor,
              whiteSpace: 'nowrap',
            }}
          >
            {t.assistantCard?.backButton || 'Вернуться к чату'}
          </span>
        </button>
      </div>

      {/* Destructive action — visually separated at bottom */}
      {onNewChat && (
        <button
          onClick={() => {
            if (!window.confirm(t.assistantCard?.newChatConfirm || 'Все сообщения будут удалены. Продолжить?')) return;
            onNewChat();
            onBack();
          }}
          style={{
            position: isNevi ? 'static' : 'absolute',
            bottom: isNevi ? undefined : '20px',
            marginTop: isNevi ? '24px' : undefined,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.6';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          <svg
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            style={{
              width: isNevi ? '16px' : '12px',
              height: isNevi ? '16px' : '12px',
              color: isNevi ? '#FF3434' : '#DC2626',
            }}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
            />
          </svg>
          <span
            style={{
              fontFamily: font,
              fontWeight: 400,
              fontSize: isNevi ? '12px' : '11px',
              lineHeight: isNevi ? '16px' : '14px',
              color: isNevi ? '#FF3434' : '#DC2626',
            }}
          >
            {t.assistantCard?.newChatButton || 'Удалить все сообщения и начать чат заново'}
          </span>
        </button>
      )}
    </div>
  );
};
